import { useState, useEffect } from 'react'
import type { TreatmentDetail } from '../../store/treatmentsSlice'

interface DiagnosisNotesProps {
  treatment: TreatmentDetail
  onUpdate: (data: Partial<TreatmentDetail>) => Promise<void>
}

export function DiagnosisNotes({ treatment, onUpdate }: DiagnosisNotesProps) {
  const [editing, setEditing] = useState(false)
  const [diagnosis, setDiagnosis] = useState(treatment.diagnosis || '')
  const [clinicalNotes, setClinicalNotes] = useState(treatment.clinical_notes || '')
  const [temperature, setTemperature] = useState(treatment.temperature != null ? String(treatment.temperature) : '')
  const [heartRate, setHeartRate] = useState(treatment.heart_rate != null ? String(treatment.heart_rate) : '')
  const [respiratoryRate, setRespiratoryRate] = useState(treatment.respiratory_rate != null ? String(treatment.respiratory_rate) : '')
  const [weightAtVisit, setWeightAtVisit] = useState(treatment.weight_at_visit != null ? String(treatment.weight_at_visit) : '')

  useEffect(() => {
    if (editing) return
    setDiagnosis(treatment.diagnosis || '')
    setClinicalNotes(treatment.clinical_notes || '')
    setTemperature(treatment.temperature != null ? String(treatment.temperature) : '')
    setHeartRate(treatment.heart_rate != null ? String(treatment.heart_rate) : '')
    setRespiratoryRate(treatment.respiratory_rate != null ? String(treatment.respiratory_rate) : '')
    setWeightAtVisit(treatment.weight_at_visit != null ? String(treatment.weight_at_visit) : '')
  }, [treatment, editing])

  const handleSave = async () => {
    await onUpdate({
      diagnosis,
      clinical_notes: clinicalNotes,
      temperature: temperature ? parseFloat(temperature) : null,
      heart_rate: heartRate ? parseInt(heartRate) : null,
      respiratory_rate: respiratoryRate ? parseInt(respiratoryRate) : null,
      weight_at_visit: weightAtVisit ? parseFloat(weightAtVisit) : null,
    })
    setEditing(false)
  }

  return (
    <div className="bg-surface rounded-[6px] border border-border-light" data-testid="diagnosis-notes-section">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-light">
        <h2 className="text-[14px] font-semibold text-text-primary">Diagnosis & Notes</h2>
        {!editing ? (
          <button
            data-testid="edit-diagnosis-button"
            onClick={() => setEditing(true)}
            className="inline-flex items-center h-7 px-3 rounded-[4px] border border-border text-[13px] text-text-secondary hover:bg-hover transition-colors duration-100 cursor-pointer"
          >
            Edit
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              data-testid="diagnosis-cancel-button"
              onClick={() => {
                setEditing(false)
                setDiagnosis(treatment.diagnosis || '')
                setClinicalNotes(treatment.clinical_notes || '')
                setTemperature(treatment.temperature != null ? String(treatment.temperature) : '')
                setHeartRate(treatment.heart_rate != null ? String(treatment.heart_rate) : '')
                setRespiratoryRate(treatment.respiratory_rate != null ? String(treatment.respiratory_rate) : '')
                setWeightAtVisit(treatment.weight_at_visit != null ? String(treatment.weight_at_visit) : '')
              }}
              className="inline-flex items-center h-7 px-3 rounded-[4px] border border-border text-[13px] text-text-secondary hover:bg-hover transition-colors duration-100 cursor-pointer"
            >
              Cancel
            </button>
            <button
              data-testid="diagnosis-save-button"
              onClick={handleSave}
              className="inline-flex items-center h-7 px-3 rounded-[4px] bg-accent text-white text-[13px] font-normal hover:bg-accent-dark transition-colors duration-100 cursor-pointer"
            >
              Save
            </button>
          </div>
        )}
      </div>

      <div className="p-4 max-sm:p-3 flex flex-col gap-4">
        <div>
          <label className="block text-[12px] text-text-muted mb-1">Diagnosis</label>
          {editing ? (
            <input
              data-testid="diagnosis-input"
              value={diagnosis}
              onChange={(e) => setDiagnosis(e.target.value)}
              placeholder="Enter diagnosis..."
              className="w-full h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
            />
          ) : (
            <p className="text-[13px] text-text-primary" data-testid="diagnosis-text">
              {treatment.diagnosis || <span className="text-text-disabled">No diagnosis entered</span>}
            </p>
          )}
        </div>

        <div>
          <label className="block text-[12px] text-text-muted mb-1">Clinical Notes</label>
          {editing ? (
            <textarea
              data-testid="clinical-notes-input"
              value={clinicalNotes}
              onChange={(e) => setClinicalNotes(e.target.value)}
              placeholder="Enter clinical notes..."
              rows={4}
              className="w-full px-2 py-1.5 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent resize-y"
            />
          ) : (
            <p className="text-[13px] text-text-primary whitespace-pre-wrap" data-testid="clinical-notes-text">
              {treatment.clinical_notes || <span className="text-text-disabled">No clinical notes entered</span>}
            </p>
          )}
        </div>

        <div>
          <label className="block text-[12px] text-text-muted mb-2">Vitals</label>
          <div className="grid grid-cols-4 max-sm:grid-cols-2 gap-3" data-testid="vitals-section">
            <div>
              <label className="block text-[11px] text-text-muted mb-1">Temperature</label>
              {editing ? (
                <input
                  data-testid="temperature-input"
                  type="number"
                  step="0.1"
                  value={temperature}
                  onChange={(e) => setTemperature(e.target.value)}
                  placeholder="°F"
                  className="w-full h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
                />
              ) : (
                <p className="text-[13px] text-text-primary" data-testid="temperature-value">
                  {treatment.temperature != null ? `${parseFloat(String(treatment.temperature))}°F` : <span className="text-text-disabled">—</span>}
                </p>
              )}
            </div>
            <div>
              <label className="block text-[11px] text-text-muted mb-1">Heart Rate</label>
              {editing ? (
                <input
                  data-testid="heart-rate-input"
                  type="number"
                  value={heartRate}
                  onChange={(e) => setHeartRate(e.target.value)}
                  placeholder="bpm"
                  className="w-full h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
                />
              ) : (
                <p className="text-[13px] text-text-primary" data-testid="heart-rate-value">
                  {treatment.heart_rate != null ? `${treatment.heart_rate} bpm` : <span className="text-text-disabled">—</span>}
                </p>
              )}
            </div>
            <div>
              <label className="block text-[11px] text-text-muted mb-1">Respiratory Rate</label>
              {editing ? (
                <input
                  data-testid="respiratory-rate-input"
                  type="number"
                  value={respiratoryRate}
                  onChange={(e) => setRespiratoryRate(e.target.value)}
                  placeholder="breaths/min"
                  className="w-full h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
                />
              ) : (
                <p className="text-[13px] text-text-primary" data-testid="respiratory-rate-value">
                  {treatment.respiratory_rate != null ? `${treatment.respiratory_rate} breaths/min` : <span className="text-text-disabled">—</span>}
                </p>
              )}
            </div>
            <div>
              <label className="block text-[11px] text-text-muted mb-1">Weight at Visit</label>
              {editing ? (
                <input
                  data-testid="weight-at-visit-input"
                  type="number"
                  step="0.01"
                  value={weightAtVisit}
                  onChange={(e) => setWeightAtVisit(e.target.value)}
                  placeholder="kg"
                  className="w-full h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
                />
              ) : (
                <p className="text-[13px] text-text-primary" data-testid="weight-at-visit-value">
                  {treatment.weight_at_visit != null ? `${parseFloat(String(treatment.weight_at_visit))} kg` : <span className="text-text-disabled">—</span>}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
